import { useMemo } from 'react'
import type { QuickBooksArTrendPeriod } from '../../../data/ServerApiClient'
import { CfoChartFrame, useChartTooltip } from './charts/chartInteraction'
import {
  arBucketSplitUsable,
  formatArCurrency,
  formatArDays,
  formatArTrendPeriodLabel,
  periodVisibleBucketAmounts,
} from './cfoArView'

// CfoArTrendChart — native stacked-aging trend with DSO + weighted-age overlays.
// Ported from the legacy A/R Review chart but rebuilt as a calm, dependency-free
// SVG: stacked Current / 1-90 / >90 columns per period, a solid DSO polyline and
// a dashed weighted-age polyline on a right-hand days axis, click-to-select a
// period, and a cursor-following tooltip. No chart libraries; colorblind-safe
// (hue + position + a dashed-vs-solid line distinction + direct labels).

const COLORS = {
  current: 'var(--pos, #2f855a)',
  under90: 'var(--check-acc, #d69e2e)',
  over90: 'var(--neg, #b91c1c)',
  discrepancy: '#94a3b8',
  dso: '#176b87',
  weightedAge: '#7a5c2e',
  axis: 'var(--axis, #cbd5e1)',
}

const periodStartKey = (period: QuickBooksArTrendPeriod): string =>
  String((period as any).periodStart ?? (period as any).period ?? '').slice(0, 10)

export type CfoArTrendChartProps = {
  periods: QuickBooksArTrendPeriod[]
  selectedPeriodStart: string
  onSelectPeriod: (periodStart: string) => void
  customerName?: string | null
  width?: number
  height?: number
  className?: string
}

export function CfoArTrendChart({
  periods,
  selectedPeriodStart,
  onSelectPeriod,
  customerName = null,
  width = 920,
  height = 280,
  className,
}: CfoArTrendChartProps) {
  const { containerRef, tip, show, hide } = useChartTooltip()

  const geometry = useMemo(() => {
    const padTop = 24
    const padBottom = 46
    const padX = 56
    const plotH = height - padTop - padBottom
    const baseY = padTop + plotH
    const count = Math.max(1, periods.length)
    const slot = (width - padX * 2) / count
    const barW = Math.min(70, slot * 0.5)

    const maxAr = Math.max(
      1,
      ...periods.map((period) => Math.max(Math.abs(Number(period.endingAr || 0)), periodVisibleBucketAmounts(period).current + periodVisibleBucketAmounts(period).under90 + periodVisibleBucketAmounts(period).over90)),
    )
    const trendValues: number[] = []
    for (const period of periods) {
      if (period.dso != null && Number.isFinite(Number(period.dso))) trendValues.push(Number(period.dso))
      if (arBucketSplitUsable(period) && period.weightedAverageAge != null && Number.isFinite(Number(period.weightedAverageAge))) {
        trendValues.push(Number(period.weightedAverageAge))
      }
    }
    const maxTrendDays = Math.max(1, ...trendValues)

    const toBarH = (value: number) => (maxAr <= 0 ? 0 : Math.max(0, (Math.max(0, value) / maxAr) * plotH))
    const toTrendY = (value: number) => baseY - (maxTrendDays <= 0 ? 0 : (Math.max(0, value) / maxTrendDays) * plotH)
    const cx = (index: number) => padX + slot * index + slot / 2

    const dsoPoints = periods
      .map((period, index) => (period.dso != null && Number.isFinite(Number(period.dso)) ? { x: cx(index), y: toTrendY(Number(period.dso)) } : null))
      .filter((point): point is { x: number; y: number } => point != null)
    const wagePoints = periods
      .map((period, index) =>
        arBucketSplitUsable(period) && period.weightedAverageAge != null && Number.isFinite(Number(period.weightedAverageAge))
          ? { x: cx(index), y: toTrendY(Number(period.weightedAverageAge)) }
          : null,
      )
      .filter((point): point is { x: number; y: number } => point != null)

    return { padTop, padBottom, padX, plotH, baseY, slot, barW, maxAr, maxTrendDays, toBarH, toTrendY, cx, dsoPoints, wagePoints }
  }, [periods, width, height])

  const polyline = (points: Array<{ x: number; y: number }>): string => points.map((point) => `${point.x},${point.y}`).join(' ')

  return (
    <CfoChartFrame containerRef={containerRef} tip={tip} className={className}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${customerName ? `${customerName} ` : 'Overall '}accounts receivable trend by aging bucket with DSO and weighted-age overlays`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        onMouseLeave={hide}
      >
        <line x1={geometry.padX} y1={geometry.baseY} x2={width - geometry.padX / 2} y2={geometry.baseY} stroke={COLORS.axis} strokeWidth={1.2} />
        {periods.map((period, index) => {
          const start = periodStartKey(period)
          const active = Boolean(selectedPeriodStart && start === selectedPeriodStart)
          const buckets = periodVisibleBucketAmounts(period)
          const bucketTotal = buckets.current + buckets.under90 + buckets.over90
          const endingAr = Math.max(0, Number(period.endingAr || 0))
          const discrepancy = Math.max(0, endingAr - bucketTotal)
          const cx = geometry.cx(index)
          const x = cx - geometry.barW / 2
          let cursorY = geometry.baseY
          const segments: Array<{ key: string; value: number; fill: string; striped?: boolean }> = [
            { key: 'current', value: buckets.current, fill: COLORS.current },
            { key: 'under90', value: buckets.under90, fill: COLORS.under90 },
            { key: 'over90', value: buckets.over90, fill: COLORS.over90 },
            { key: 'discrepancy', value: discrepancy, fill: COLORS.discrepancy, striped: true },
          ]
          return (
            <g
              key={`ar-trend-${start || index}`}
              role="button"
              tabIndex={0}
              aria-pressed={active}
              aria-label={`${formatArTrendPeriodLabel(period)} ending A/R ${formatArCurrency(period.endingAr)}`}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelectPeriod(active ? '' : start)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelectPeriod(active ? '' : start)
                }
              }}
              onMouseMove={(event) =>
                show(
                  event.clientX,
                  event.clientY,
                  formatArCurrency(period.endingAr),
                  `${formatArTrendPeriodLabel(period)} · DSO ${formatArDays(period.dso)}`,
                )
              }
            >
              {active ? (
                <rect x={x - 5} y={geometry.padTop - 6} width={geometry.barW + 10} height={geometry.plotH + 12} rx={6} fill="var(--accent-wash, rgba(23,107,135,0.08))" />
              ) : null}
              {segments.map((segment) => {
                const h = geometry.toBarH(segment.value)
                if (h <= 0) return null
                cursorY -= h
                const segY = cursorY
                return segment.striped ? (
                  <rect key={segment.key} x={x} y={segY} width={geometry.barW} height={h} fill={segment.fill} opacity={0.55} />
                ) : (
                  <rect key={segment.key} x={x} y={segY} width={geometry.barW} height={h} rx={2} fill={segment.fill} />
                )
              })}
              <text x={cx} y={geometry.baseY - geometry.toBarH(Math.max(endingAr, bucketTotal)) - 7} textAnchor="middle" style={{ font: '700 10px var(--font-ui)', fill: 'var(--ink-2, #4c5360)' }}>
                {formatArCurrency(period.endingAr)}
              </text>
              <text x={cx} y={height - 24} textAnchor="middle" style={{ font: `${active ? '700' : '500'} 11px var(--font-guide)`, fontStyle: 'italic', fill: active ? 'var(--accent, #176b87)' : 'var(--muted, #868c9a)' }}>
                {formatArTrendPeriodLabel(period)}
              </text>
            </g>
          )
        })}

        {geometry.wagePoints.length > 1 ? (
          <polyline points={polyline(geometry.wagePoints)} fill="none" stroke={COLORS.weightedAge} strokeWidth={2} strokeDasharray="7 6" />
        ) : null}
        {geometry.wagePoints.map((point, index) => (
          <circle key={`wage-${index}`} cx={point.x} cy={point.y} r={3.5} fill={COLORS.weightedAge} />
        ))}
        {geometry.dsoPoints.length > 1 ? (
          <polyline points={polyline(geometry.dsoPoints)} fill="none" stroke={COLORS.dso} strokeWidth={2.4} />
        ) : null}
        {geometry.dsoPoints.map((point, index) => (
          <circle key={`dso-${index}`} cx={point.x} cy={point.y} r={4} fill={COLORS.dso} />
        ))}

        <text x={width - geometry.padX / 2} y={geometry.padTop + 4} textAnchor="end" style={{ font: '600 10px var(--font-ui)', fill: COLORS.dso }}>
          {formatArDays(geometry.maxTrendDays)}
        </text>
        <text x={width - geometry.padX / 2} y={geometry.baseY - 2} textAnchor="end" style={{ font: '600 10px var(--font-ui)', fill: 'var(--muted, #868c9a)' }}>
          0 days
        </text>
      </svg>
    </CfoChartFrame>
  )
}

export default CfoArTrendChart
